import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * The formation, drawn as the journey it is.
 *
 * These milestones carry real order and real dates — submitted, name cleared,
 * filed, certificate, EIN. Rendering them as a list of rows throws all of that
 * away: a list says "here are six facts", a track says "you are here, that is
 * behind you, this is next", which is the actual question someone opens this
 * page to answer.
 *
 * State is carried by shape as well as colour — filled, ringed, hollow — so it
 * still reads for someone who cannot tell the green from the grey.
 */

export type TrackStep = {
  key: string
  label: string
  detail?: string
  state: 'done' | 'current' | 'upcoming'
}

export function MilestoneTrack({ steps, className }: { steps: TrackStep[]; className?: string }) {
  return (
    <ol className={cn('relative', className)}>
      {steps.map((step, i) => {
        const isLast = i === steps.length - 1
        const done = step.state === 'done'
        const current = step.state === 'current'

        return (
          <li key={step.key} className="relative flex gap-4 pb-6 last:pb-0">
            {/* The path. Solid behind you, dotted ahead — so the line itself
                tells you how far along you are, not just the nodes on it. */}
            {!isLast && (
              <span
                aria-hidden="true"
                className={cn(
                  'absolute left-[11px] top-6 bottom-0 w-px',
                  done ? 'bg-foreground/25' : 'border-l border-dashed border-border'
                )}
              />
            )}

            <span
              aria-hidden="true"
              className={cn(
                'relative z-10 mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full',
                done && 'bg-foreground text-background',
                current && 'border-2 border-foreground bg-background',
                !done && !current && 'border border-dashed border-muted-foreground/50 bg-background'
              )}
            >
              {done && <Check className="size-3.5" strokeWidth={3} />}
              {current && <span className="size-2 rounded-full bg-foreground" />}
            </span>

            <div className="min-w-0 flex-1 pt-0.5">
              <p
                className={cn(
                  'text-sm leading-tight',
                  current ? 'font-medium text-foreground' : done ? 'text-foreground' : 'text-muted-foreground'
                )}
              >
                {step.label}
                {/* The node's shape carries the state visually, but a shape is
                    nothing to a screen reader — the word has to be in the text.
                    Visible for the current step because that is the one someone
                    is looking for; announced for the rest. */}
                {current ? (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">in progress</span>
                ) : (
                  <span className="sr-only">{done ? 'Done' : 'Not started yet'}</span>
                )}
              </p>
              {step.detail && (
                <p className="mt-1 text-xs leading-snug text-muted-foreground">{step.detail}</p>
              )}
            </div>
          </li>
        )
      })}
    </ol>
  )
}
